// client/src/lib/api/api-client.ts
// ===========================================
// ARCHIVO BASE PARA REQUESTS HTTP
// Centraliza autenticación, errores y construcción de URLs
// ===========================================

import { getCurrentUser, getCurrentShop } from "@/lib/auth";

// -------------------------------------------
// Helper para obtener headers con autenticación
// -------------------------------------------
// COMENTARIO: Esta función centraliza los headers que necesitan todas las peticiones.
// Incluye x-user-id y x-shop-id si están disponibles, evitando repetir lógica en cada función.
export const getAuthHeaders = (): HeadersInit => {
  const user = getCurrentUser();
  const shop = getCurrentShop();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  // COMENTARIO: Solo agregamos los headers si existen los valores, evitando enviar "undefined"
  if (user?.id) headers["x-user-id"] = user.id;
  if (shop?.id) headers["x-shop-id"] = shop.id;

  return headers;
};

// -------------------------------------------
// Clase para errores de API (tipada y consistente)
// -------------------------------------------
// COMENTARIO: Esta clase permite manejar errores con información estructurada:
// - message: mensaje legible para el usuario
// - status: código HTTP para lógica condicional
// - data: respuesta completa del backend para debug
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// -------------------------------------------
// Función base para requests HTTP
// -------------------------------------------
// COMENTARIO: Esta función envuelve fetch() para:
// 1. Inyectar automáticamente los headers de autenticación
// 2. Manejar errores de forma uniforme con ApiError
// 3. Parsear JSON de forma segura
// 4. Manejar respuestas vacías (204 No Content)
export async function apiRequest<T>(
  url: string,
  options: RequestInit = {},
): Promise<T> {
  // COMENTARIO: Fusionamos los headers: los de auth por defecto + los que pase el caller
  const config: RequestInit = {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  };

  const response = await fetch(url, config);

  // COMENTARIO: Intentamos leer el cuerpo como JSON para obtener mensajes de error detallados
  // Usamos clone() porque el body de una respuesta solo se puede leer una vez
  let errorData: any;
  try {
    errorData = await response.clone().json();
  } catch {
    errorData = null;
  }

  // COMENTARIO: Si la respuesta no es exitosa, lanzamos un error tipado con toda la información
  if (!response.ok) {
    // Combinar error + details para no perder información
    const errorMessage =
      errorData?.error || errorData?.message || `Error ${response.status}`;
    const fullMessage = errorData?.details
      ? `${errorMessage}: ${errorData.details}`
      : errorMessage;

    throw new ApiError(fullMessage, response.status, errorData);
  }

  // COMENTARIO: Para respuestas 204 (No Content), retornamos undefined en lugar de intentar parsear JSON
  if (response.status === 204) {
    return undefined as T;
  }

  // COMENTARIO: Parseamos y retornamos la respuesta como JSON tipado
  return response.json();
}

// -------------------------------------------
// Helper para construir URLs con parámetros de query
// -------------------------------------------
// COMENTARIO: Evita errores manuales al concatenar ? y & en las URLs.
// Ignora automáticamente valores undefined, null o vacíos.
export function buildUrlWithParams(
  baseUrl: string,
  params?: Record<string, string | number | boolean | undefined | null>,
): string {
  // COMENTARIO: Si no hay params, retornamos la URL base sin modificar
  if (!params) return baseUrl;

  const searchParams = new URLSearchParams();

  // COMENTARIO: Iteramos sobre cada parámetro y solo agregamos los que tengan valor válido
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });

  // COMENTARIO: Si hay parámetros, los concatenamos con ?; si no, retornamos la URL base
  const queryString = searchParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}
